import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Divider,
	Popper,
	Fade,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { Check } from '@material-ui/icons';
import React, { useState, useEffect, useRef } from 'react';
import AscendingSortIcon from '@shared/icons/AscendingSortIcon';
import DescendingSortIcon from '@shared/icons/DescendingSortIcon';

const CameraListingTableHead = ({
	columns,
	sort,
	order,
	onRequestSort,
	studentOrder,
	onRequestStudentNameOrderChange,
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [hoveredColumn, setHoveredColumn] = useState(null);

	const handlePopoverOpen = (event, columnId) => {
		setAnchorEl(event.currentTarget);
		setHoveredColumn(columnId);
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
		setHoveredColumn(null);
	};

	const open = Boolean(anchorEl);

	const handleStudentNameOrderChange = (order, nameOrder) => {
		onRequestStudentNameOrderChange(order, nameOrder);
		handlePopoverClose();
	};

	const handleColumnOrderChange = (order, columnId) => {
		onRequestSort(order, columnId);
		handlePopoverClose();
	};

	return (
		<TableHead>
			<TableRow style={{ borderBottom: '1px solid #DBDBDB', background: '#F5F5F5' }}>
				{columns.map((headCell) => {
					const isStudentHeader = headCell.id === 'student';
					const isStudentSort = sort === 'student';
					const columnsWithSort = [
						'parent',
						'student',
						'room',
						'package_type',
						'subscription_date',
						'renewal_date',
					];

					return (
						<TableCell
							className={`text-white  py-3 font-bold bg-[#002E6C] ${headCell.widthClass}`}
							key={headCell.id}
							onMouseEnter={(event) => handlePopoverOpen(event, headCell.id)}
							onMouseLeave={handlePopoverClose}
							scope="col"
						>
							<span className="text-md">{headCell.label}</span>

							{columnsWithSort.includes(headCell.id) ? (
								<Popper
									anchorEl={anchorEl}
									onClose={handlePopoverClose}
									open={open ? hoveredColumn === headCell.id : null}
									placement="bottom-start"
									transition
								>
									{({ TransitionProps }) => (
										<Fade {...TransitionProps}>
											<div className="flex flex-col p-4 min-w-216 bg-white shadow-xl">
												{isStudentHeader ? (
													<>
														<button
															className="flex items-center p-4 hover:bg-gray-100"
															onClick={() => handleStudentNameOrderChange('asc', 'last')}
															type="button"
														>
															<AscendingSortIcon />
															<span className="">Sort Ascending</span>
															<Check
																className={`absolute right-10 ${
																	isStudentSort &&
																	studentOrder === 'last' &&
																	order === 'asc'
																		? 'text-black'
																		: 'text-transparent'
																}`}
															/>
														</button>
														<Divider />
														<button
															className="flex items-center p-4 hover:bg-gray-100 active:bg-gray-200"
															onClick={() => handleStudentNameOrderChange('desc', 'last')}
															type="button"
														>
															<DescendingSortIcon />
															<span>Sort Descending</span>
															<Check
																className={`absolute right-10 ${
																	isStudentSort &&
																	studentOrder === 'last' &&
																	order === 'desc'
																		? 'text-black'
																		: 'text-transparent'
																}`}
															/>
														</button>
													</>
												) : (
													<>
														<button
															className="flex items-center p-4 hover:bg-gray-100"
															onClick={() => handleColumnOrderChange('asc', headCell.id)}
															type="button"
														>
															<AscendingSortIcon />
															<span className="">Sort Ascending</span>
															<Check
																className={`absolute right-10 ${
																	sort === headCell.id && order === 'asc'
																		? 'text-black'
																		: 'text-transparent'
																}`}
															/>
														</button>
														<Divider />
														<button
															className="flex items-center p-4 hover:bg-gray-100 active:bg-gray-200"
															onClick={() => handleColumnOrderChange('desc', headCell.id)}
															type="button"
														>
															<DescendingSortIcon />
															<span>Sort Descending</span>
															<Check
																className={`absolute right-10 ${
																	sort === headCell.id && order === 'desc'
																		? 'text-black'
																		: 'text-transparent'
																}`}
															/>
														</button>
													</>
												)}
											</div>
										</Fade>
									)}
								</Popper>
							) : null}
						</TableCell>
					);
				})}
			</TableRow>
		</TableHead>
	);
};

const CameraListingTable = ({ data, page, setPage, columns, totalResults, filters, setFilters }) => {
	const tableContainerRef = useRef(null);
	const handleRequestSort = (dir, property) => {
		setFilters({ ...filters, dir: dir, sort: property });
	};

	const handleChangePage = (_, newPage) => {
		setPage(newPage);
	};

	const onRequestStudentNameOrderChange = (order, nameOrder) => {
		setFilters({ ...filters, sort: 'student', student_name: nameOrder, dir: order });
	};
	useEffect(() => {
		if (tableContainerRef.current) {
			tableContainerRef.current.scrollTop = 0;
		}
	}, [filters]);

	return (
		<div className={`relative ${data?.length ? 'mb-96' : ''}`}>
			<TableContainer
				className="max-w-full flex-col w-full flex  whitespace-no-wrap relative min-h-512"
				ref={tableContainerRef}
			>
				<Table stickyHeader>
					<CameraListingTableHead
						columns={columns}
						onRequestSort={handleRequestSort}
						onRequestStudentNameOrderChange={onRequestStudentNameOrderChange}
						order={filters?.dir || 'asc'}
						sort={filters?.sort || 'room_name'}
						studentOrder={filters?.student_name}
					/>
					<TableBody>
						{data?.map((row, index) => {
							return (
								<TableRow key={`${row.id}${row.created_at}${index}`}>
									{columns.map((column) => (
										<TableCell className="cursor-default" key={column.id}>
											{column.render(row)}
										</TableCell>
									))}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<div className="flex justify-center  sticky bottom-0 left-0 bg-white right-0">
				{data?.length ? (
					<Pagination
						color="primary"
						count={Math.ceil(totalResults / 20)}
						onChange={handleChangePage}
						page={page}
					/>
				) : null}
			</div>
		</div>
	);
};

export default CameraListingTable;
